import type { BookingDetailsCancellationOptions, BookingDetailsState, FetchRequestStatus } from '../../domain/types'
import type { CancelBookingOption } from '../components/CancelBookingAction/types'
import { isActiveUpcomingBooking } from '@/scopes/account/common/utils/bookingDates'
import { isVoucherCancellationUrlValid } from '../../domain/policies/voucherCancellationUrl'
import { formatParticipantFullName } from '../utils/participantName'
import { getParticipantFullName, getParticipantsByStatus } from './participantsSelectors'

export function getIsCancelBookingDialogOpen(state: BookingDetailsState): boolean {
  return state.isCancelBookingDialogOpen
}

interface CancelBookingOptionRule {
  option: CancelBookingOption
  isAvailable: (cancellationOptions: BookingDetailsCancellationOptions) => boolean
}

const CANCEL_BOOKING_OPTION_RULES = [
  { option: 'voucher', isAvailable: options => isVoucherCancellationUrlValid(options.voucherCancellationUrl) },
  { option: 'exchange', isAvailable: options => options.isRebookable },
  { option: 'partial-cancellation', isAvailable: options => options.isPartialCancellable },
  { option: 'full-cancellation', isAvailable: options => options.isFullCancellable },
] as const satisfies ReadonlyArray<CancelBookingOptionRule>

export function getAvailableCancelBookingOptions(state: BookingDetailsState): CancelBookingOption[] {
  const { cancellationOptions } = state

  return CANCEL_BOOKING_OPTION_RULES
    .filter(rule => rule.isAvailable(cancellationOptions))
    .map(rule => rule.option)
}

export function getIsCancelBookingActionShown(state: BookingDetailsState, now: Date = new Date()): boolean {
  return isActiveUpcomingBooking(state.rawDepartureDate, state.isCancelled, now)
    && getAvailableCancelBookingOptions(state).length > 0
}

export function getIsCancelBookingMainBookerSelected(
  state: BookingDetailsState,
  participantIds: string[],
): boolean {
  const selectedParticipantIds = new Set(participantIds)
  const { active: participants } = getParticipantsByStatus(state)

  return participants.some(
    participant => selectedParticipantIds.has(participant.id) && participant.type === 'MainBooker',
  )
}

export function getCancelBookingSelectedParticipantNames(
  state: BookingDetailsState,
  participantIds: string[],
  resolveTitle: (titleKey: string) => string,
): string {
  const selectedParticipantIds = new Set(participantIds)
  const { active: participants } = getParticipantsByStatus(state)

  return participants
    .filter(participant => selectedParticipantIds.has(participant.id))
    .map((participant) => {
      const { titleKey, firstName, lastName } = getParticipantFullName(participant)

      return formatParticipantFullName(
        titleKey ? resolveTitle(titleKey) : null,
        firstName,
        lastName,
      )
    })
    .join(', ')
}

export interface PartialCancellationFeeItem {
  participantId: string
  name: string
  cancellationCost: number | null
  isComplete: boolean
}

export interface PartialCancellationFeeItems {
  status: FetchRequestStatus
  isFree: boolean
  items: PartialCancellationFeeItem[]
}

export function getPartialCancellationFeeItems(
  state: BookingDetailsState,
  participantIds: string[],
  resolveTitle: (titleKey: string) => string,
): PartialCancellationFeeItems {
  const { status, participants: feeParticipants, isFreeCancellationAllowed } = state.partialCancellationFee
  const { active: participants } = getParticipantsByStatus(state)
  const participantsById = new Map(participants.map(participant => [participant.id, participant]))

  const items: PartialCancellationFeeItem[] = participantIds.flatMap((participantId): PartialCancellationFeeItem[] => {
    const participant = participantsById.get(participantId)
    if (!participant)
      return []

    const { titleKey, firstName, lastName } = getParticipantFullName(participant)
    const name = formatParticipantFullName(
      titleKey ? resolveTitle(titleKey) : null,
      firstName,
      lastName,
    )

    if (status !== 'success' || isFreeCancellationAllowed) {
      return [{
        participantId,
        name,
        cancellationCost: null,
        isComplete: status === 'success',
      }]
    }

    const feeData = feeParticipants.find(fee => fee.participantId === participantId)
    if (!feeData) {
      return [{ participantId, name, cancellationCost: null, isComplete: false }]
    }

    return [{ participantId, name, cancellationCost: feeData.cancellationCost, isComplete: true }]
  })

  return {
    status,
    isFree: status === 'success' && isFreeCancellationAllowed,
    items,
  }
}
